import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  createPartnerSponsorshipAsset,
  deletePartnerSponsorshipAsset,
  getPartnerPortalDashboard,
  listPartnerSponsorshipAssets,
  submitPartnerSponsorshipAsset,
  updatePartnerSponsorshipAsset,
} from "../../api/partnerPortalApi";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import { AUTOMAP_CLIENT_VIEW_SLUG } from "../../constants/automapPartnerPortalConfig";
import "../../styles/sponsorshipAssetsPage.css";

const WORKFLOW_STEPS = [
  {
    step: 1,
    title: "Upload creative",
    detail: "Add the image, banner, or badge file that should appear in sponsored inventory on AutoMapa.",
  },
  {
    step: 2,
    title: "Submit for review",
    detail: "Midpoint operators approve assets before they can go live. Drafts stay private until you submit them.",
  },
  {
    step: 3,
    title: "Attach to a placement",
    detail: "After approval, choose the asset when scheduling a placement under Sponsorship placements or Campaigns.",
  },
];

const ASSET_TYPES = [
  { value: "logo", label: "Logo", hint: "Dealer or partner mark for cards and badges." },
  { value: "banner", label: "Banner", hint: "Wide hero or strip creative for high-visibility slots." },
  { value: "card_image", label: "Card image", hint: "Square or listing-card artwork for search results." },
  { value: "listing_badge", label: "Listing badge", hint: "Small overlay such as “Sponsored” or a promo ribbon." },
  { value: "video", label: "Video", hint: "Hosted video URL for rich placements." },
  { value: "document", label: "Document", hint: "PDF or brochure linked from a placement." },
  { value: "other", label: "Other", hint: "Any other approved creative format." },
];

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "draft", label: "Drafts" },
  { value: "submitted", label: "Pending approval" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const EMPTY_FORM = {
  clientViewId: "",
  assetType: "banner",
  title: "",
  description: "",
  fileUrl: "",
  targetUrl: "",
};

function normalizeAsset(row) {
  if (!row) return null;
  return {
    id: row.id,
    clientViewId: row.client_view_id || row.clientViewId,
    clientViewName: row.client_view_name || row.clientViewName,
    assetType: row.asset_type || row.assetType,
    title: row.title,
    description: row.description,
    fileUrl: row.file_url || row.fileUrl,
    targetUrl: row.target_url || row.targetUrl,
    status: row.status,
    submittedAt: row.submitted_at || row.submittedAt,
    reviewedAt: row.reviewed_at || row.reviewedAt,
    updatedAt: row.updated_at || row.updatedAt,
  };
}

function assetTypeLabel(value) {
  return ASSET_TYPES.find((type) => type.value === value)?.label || value || "—";
}

function statusLabel(status) {
  switch (status) {
    case "draft":
      return "Draft";
    case "submitted":
      return "Pending approval";
    case "approved":
      return "Approved";
    case "rejected":
      return "Rejected";
    case "archived":
      return "Archived";
    default:
      return status || "—";
  }
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function isImageUrl(url) {
  if (!url) return false;
  return /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url);
}

function AssetPreview({ asset }) {
  if (isImageUrl(asset.fileUrl)) {
    return (
      <img
        src={asset.fileUrl}
        alt=""
        className="sponsorship-assets-page__preview-image"
      />
    );
  }

  return (
    <div className="sponsorship-assets-page__preview-fallback" aria-hidden="true">
      {assetTypeLabel(asset.assetType)}
    </div>
  );
}

const PartnerSponsorshipAssetsPage = () => {
  const { path, clientViewSlug } = usePartnerPortalBase();
  const [assets, setAssets] = useState([]);
  const [clientViews, setClientViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setErrorMessage("");
    try {
      const [assetsBody, dashboardBody] = await Promise.all([
        listPartnerSponsorshipAssets(),
        getPartnerPortalDashboard(),
      ]);
      const rows = (assetsBody.assets || []).map(normalizeAsset).filter(Boolean);
      setAssets(rows);
      const views = dashboardBody.clientViews || [];
      setClientViews(views);
      setForm((current) => {
        if (current.clientViewId) return current;
        const preferred =
          views.find((view) => view.slug === clientViewSlug)?.clientViewId ||
          views.find((view) => view.slug === AUTOMAP_CLIENT_VIEW_SLUG)?.clientViewId ||
          views[0]?.clientViewId ||
          "";
        return { ...current, clientViewId: preferred };
      });
    } catch (error) {
      setAssets([]);
      setErrorMessage(error.message || "Unable to load sponsorship assets.");
    } finally {
      setLoading(false);
    }
  }, [clientViewSlug]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const summary = useMemo(() => {
    const counts = {
      total: assets.length,
      draft: 0,
      submitted: 0,
      approved: 0,
      rejected: 0,
    };
    for (const asset of assets) {
      if (counts[asset.status] != null) {
        counts[asset.status] += 1;
      }
    }
    return counts;
  }, [assets]);

  const filteredAssets = useMemo(() => {
    if (statusFilter === "all") return assets;
    return assets.filter((asset) => asset.status === statusFilter);
  }, [assets, statusFilter]);

  const selectedType = ASSET_TYPES.find((type) => type.value === form.assetType);

  const resetForm = () => {
    setEditingId(null);
    setForm((current) => ({
      ...EMPTY_FORM,
      clientViewId: current.clientViewId,
      assetType: "banner",
    }));
  };

  const startEdit = (asset) => {
    setEditingId(asset.id);
    setForm({
      clientViewId: asset.clientViewId || "",
      assetType: asset.assetType || "banner",
      title: asset.title || "",
      description: asset.description || "",
      fileUrl: asset.fileUrl || "",
      targetUrl: asset.targetUrl || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = async (submitAfterSave = false) => {
    if (!form.clientViewId || !form.title.trim() || !form.fileUrl.trim()) {
      setToast("Client view, title, and file URL are required.");
      return;
    }

    setSaving(true);
    setErrorMessage("");
    try {
      const payload = {
        clientViewId: form.clientViewId,
        assetType: form.assetType,
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        fileUrl: form.fileUrl.trim(),
        targetUrl: form.targetUrl.trim() || undefined,
      };

      let assetId = editingId;
      if (editingId) {
        await updatePartnerSponsorshipAsset(editingId, payload);
      } else {
        const body = await createPartnerSponsorshipAsset(payload);
        assetId = normalizeAsset(body.asset)?.id;
      }

      if (submitAfterSave && assetId) {
        await submitPartnerSponsorshipAsset(assetId);
        setToast("Asset saved and submitted for approval.");
      } else {
        setToast(editingId ? "Asset updated." : "Draft saved.");
      }

      resetForm();
      await load();
    } catch (error) {
      setToast(error.message || "Unable to save asset.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitExisting = async (assetId) => {
    setSaving(true);
    try {
      await submitPartnerSponsorshipAsset(assetId);
      setToast("Asset submitted for approval.");
      await load();
    } catch (error) {
      setToast(error.message || "Unable to submit asset.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm("Delete this draft asset? This cannot be undone.")) return;
    setSaving(true);
    try {
      await deletePartnerSponsorshipAsset(assetId);
      setToast("Asset deleted.");
      if (editingId === assetId) resetForm();
      await load();
    } catch (error) {
      setToast(error.message || "Unable to delete asset.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="muted panel-card">Loading sponsorship assets…</p>;
  }

  return (
    <div className="sponsorship-assets-page">
      <header className="sponsorship-assets-page__hero panel-card">
        <div>
          <p className="sponsorship-assets-page__eyebrow">Sponsorship · Creative library</p>
          <h1>Sponsorship assets</h1>
          <p className="sponsorship-assets-page__lede">
            Upload and manage the images, banners, and badges used in sponsored AutoMapa inventory.
            Assets must be approved before they can be attached to a placement.
          </p>
        </div>
        <div className="sponsorship-assets-page__hero-links">
          <Link className="sponsorship-assets-page__link-card" to={path("sponsorship-placements")}>
            <strong>Placements</strong>
            <span>Schedule where sponsored units appear on the site.</span>
          </Link>
          <Link className="sponsorship-assets-page__link-card" to={path("campaigns")}>
            <strong>Campaigns</strong>
            <span>Group placements for reporting and billing.</span>
          </Link>
        </div>
      </header>

      <section className="sponsorship-assets-page__workflow panel-card">
        <h2>How this fits together</h2>
        <ol className="sponsorship-assets-page__workflow-steps">
          {WORKFLOW_STEPS.map((step) => (
            <li key={step.step}>
              <span className="sponsorship-assets-page__workflow-number">{step.step}</span>
              <div>
                <strong>{step.title}</strong>
                <p>{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="sponsorship-assets-page__summary-grid">
        <article className="sponsorship-assets-page__summary-card">
          <span className="sponsorship-assets-page__summary-label">Total assets</span>
          <strong>{summary.total}</strong>
        </article>
        <article className="sponsorship-assets-page__summary-card">
          <span className="sponsorship-assets-page__summary-label">Approved</span>
          <strong>{summary.approved}</strong>
        </article>
        <article className="sponsorship-assets-page__summary-card sponsorship-assets-page__summary-card--alert">
          <span className="sponsorship-assets-page__summary-label">Pending approval</span>
          <strong>{summary.submitted}</strong>
        </article>
        <article className="sponsorship-assets-page__summary-card">
          <span className="sponsorship-assets-page__summary-label">Drafts</span>
          <strong>{summary.draft}</strong>
        </article>
      </section>

      <section className="sponsorship-assets-page__form panel-card">
        <header className="sponsorship-assets-page__form-header">
          <div>
            <h2>{editingId ? "Edit asset" : "Add asset"}</h2>
            <p className="muted">
              {editingId
                ? "Update the creative, then save or submit again for review."
                : "Save a draft to review internally, or submit immediately for operator approval."}
            </p>
          </div>
          {editingId ? (
            <button type="button" className="sponsorship-assets-page__btn" onClick={resetForm}>
              Cancel edit
            </button>
          ) : null}
        </header>

        {errorMessage ? <p className="sponsorship-assets-page__error">{errorMessage}</p> : null}

        <form
          className="sponsorship-assets-page__form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            handleSave(false);
          }}
        >
          <label className="sponsorship-assets-page__field">
            <span>Client view</span>
            <select
              value={form.clientViewId}
              onChange={(event) => setForm({ ...form, clientViewId: event.target.value })}
              required
            >
              <option value="">Select client view</option>
              {clientViews.map((view) => (
                <option key={view.clientViewId} value={view.clientViewId}>
                  {view.name || view.slug}
                </option>
              ))}
            </select>
          </label>

          <label className="sponsorship-assets-page__field">
            <span>Asset type</span>
            <select
              value={form.assetType}
              onChange={(event) => setForm({ ...form, assetType: event.target.value })}
            >
              {ASSET_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {selectedType ? <small className="muted">{selectedType.hint}</small> : null}
          </label>

          <label className="sponsorship-assets-page__field sponsorship-assets-page__field--wide">
            <span>Title</span>
            <input
              value={form.title}
              onChange={(event) => setForm({ ...form, title: event.target.value })}
              placeholder="e.g. Q3 dealer spotlight banner"
              required
            />
          </label>

          <label className="sponsorship-assets-page__field sponsorship-assets-page__field--wide">
            <span>File URL</span>
            <input
              value={form.fileUrl}
              onChange={(event) => setForm({ ...form, fileUrl: event.target.value })}
              placeholder="https://cdn.example.com/sponsorship/banner.jpg"
              required
            />
            <small className="muted">Public HTTPS URL to the image, video, or document.</small>
          </label>

          <label className="sponsorship-assets-page__field sponsorship-assets-page__field--wide">
            <span>Click-through URL (optional)</span>
            <input
              value={form.targetUrl}
              onChange={(event) => setForm({ ...form, targetUrl: event.target.value })}
              placeholder="https://automap.example/dealer/special"
            />
          </label>

          <label className="sponsorship-assets-page__field sponsorship-assets-page__field--wide">
            <span>Internal notes (optional)</span>
            <textarea
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              rows={3}
              placeholder="Campaign context, sizing notes, or approval instructions."
            />
          </label>

          <div className="sponsorship-assets-page__form-actions">
            <button
              type="submit"
              className="sponsorship-assets-page__btn"
              disabled={saving}
            >
              {saving ? "Saving…" : editingId ? "Save changes" : "Save draft"}
            </button>
            <button
              type="button"
              className="sponsorship-assets-page__btn sponsorship-assets-page__btn--primary"
              disabled={saving}
              onClick={() => handleSave(true)}
            >
              {saving ? "Saving…" : editingId ? "Save & submit" : "Save & submit for approval"}
            </button>
          </div>
        </form>
      </section>

      {toast ? <p className="sponsorship-assets-page__toast panel-card">{toast}</p> : null}

      <section className="sponsorship-assets-page__library panel-card">
        <header className="sponsorship-assets-page__library-header">
          <div>
            <h2>Asset library</h2>
            <p className="muted">
              Only <strong>Approved</strong> assets can be linked from placements. Rejected assets can
              be edited and resubmitted.
            </p>
          </div>
          <div className="sponsorship-assets-page__filters" role="tablist" aria-label="Asset status">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                role="tab"
                aria-selected={statusFilter === filter.value}
                className={`sponsorship-assets-page__filter${
                  statusFilter === filter.value ? " is-active" : ""
                }`}
                onClick={() => setStatusFilter(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </header>

        {filteredAssets.length === 0 ? (
          <div className="sponsorship-assets-page__empty">
            <h3>No assets in this view</h3>
            <p className="muted">
              {statusFilter === "all"
                ? "Upload your first creative above to start the approval workflow."
                : `No assets with status “${statusLabel(statusFilter)}”.`}
            </p>
          </div>
        ) : (
          <div className="sponsorship-assets-page__cards">
            {filteredAssets.map((asset) => {
              const canEdit = asset.status === "draft" || asset.status === "rejected";
              const canSubmit = canEdit;
              const canDelete = asset.status === "draft" || asset.status === "rejected";

              return (
                <article key={asset.id} className="sponsorship-assets-page__card">
                  <div className="sponsorship-assets-page__card-preview">
                    <AssetPreview asset={asset} />
                  </div>
                  <div className="sponsorship-assets-page__card-body">
                    <div className="sponsorship-assets-page__card-top">
                      <div>
                        <h3>{asset.title}</h3>
                        <p className="sponsorship-assets-page__card-meta">
                          {assetTypeLabel(asset.assetType)}
                          <span aria-hidden="true"> · </span>
                          {asset.clientViewName || "Client view"}
                        </p>
                      </div>
                      <span
                        className={`sponsorship-assets-page__status sponsorship-assets-page__status--${asset.status}`}
                      >
                        {statusLabel(asset.status)}
                      </span>
                    </div>

                    {asset.description ? (
                      <p className="sponsorship-assets-page__card-notes">{asset.description}</p>
                    ) : null}

                    <dl className="sponsorship-assets-page__card-details">
                      <div>
                        <dt>File</dt>
                        <dd>
                          {asset.fileUrl ? (
                            <a href={asset.fileUrl} target="_blank" rel="noopener noreferrer">
                              Open file
                            </a>
                          ) : (
                            "—"
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>Click-through</dt>
                        <dd>
                          {asset.targetUrl ? (
                            <a href={asset.targetUrl} target="_blank" rel="noopener noreferrer">
                              Open link
                            </a>
                          ) : (
                            "—"
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>Updated</dt>
                        <dd>{formatDate(asset.updatedAt)}</dd>
                      </div>
                    </dl>

                    <div className="sponsorship-assets-page__card-actions">
                      {canEdit ? (
                        <button
                          type="button"
                          className="sponsorship-assets-page__btn"
                          onClick={() => startEdit(asset)}
                        >
                          Edit
                        </button>
                      ) : null}
                      {canSubmit ? (
                        <button
                          type="button"
                          className="sponsorship-assets-page__btn sponsorship-assets-page__btn--primary"
                          disabled={saving}
                          onClick={() => handleSubmitExisting(asset.id)}
                        >
                          Submit for approval
                        </button>
                      ) : null}
                      {asset.status === "approved" ? (
                        <Link
                          className="sponsorship-assets-page__btn"
                          to={path("sponsorship-placements")}
                        >
                          Use in placement
                        </Link>
                      ) : null}
                      {canDelete ? (
                        <button
                          type="button"
                          className="sponsorship-assets-page__btn sponsorship-assets-page__btn--danger"
                          disabled={saving}
                          onClick={() => handleDelete(asset.id)}
                        >
                          Delete
                        </button>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default PartnerSponsorshipAssetsPage;
